import { useNavigate } from "react-router-dom";
import { askStarters, type AskAction } from "../../chat/askStarters";
import { AskProcessActionSetup } from "./AskProcessActionSetup";
import { ChartNoAxesColumn, RotateCcw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAskSidebar, type ContextualAsk } from "../../chat/AskContext";
import { defaultAskSuggestions, editableScope, filtersForScope } from "../../chat/askScope";
import { playScriptedReply, useChatSession } from "../../chat/chatStore";
import { setChatDraft } from "../../chat/chatDraft";
import {
  addRecentQuestion,
  attachQuestionAlert,
  useRecentQuestions,
  questionChatKey,
  recentQuestionsSnapshot,
  touchRecentQuestion,
} from "../../watchlist/recentQuestions";
import { hasProcessSelection } from "../../watchlist/types";
import { useWatchContext } from "../../watchlist/useWatchFlow";
import { useAlerts } from "../../watchlist/hooks";
import ConversationAlert from "./ConversationAlert";
import FollowUpAction from "./FollowUpAction";
import AskScopeControls from "./AskScopeControls";
import { filtersWithMention } from "./askMentions";
import { useAskScopeUpdate } from "./useAskScopeUpdate";
import { ChatPanel } from "./ChatPanel";
import { AskWatchSetup } from "./AskWatchSetup";

export default function AskConversation({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const ask = useAskSidebar();
  const navigate = useNavigate();
  const recent = useRecentQuestions().find(question => question.id === ask.conversationId);
  const alert = useAlerts().find(alert => alert.id === recent?.alertId);
  const context = useWatchContext({ defaultWindowDays: 30 });
  const { userOptions, processOptions, scopeSummary } = context;
  const [anchor] = useState(() => Date.now());
  // Hiding the KPI offer lasts for the conversation, so it does not return after every answer.
  const [kpiOfferHiddenIn, setKpiOfferHiddenIn] = useState<string>();
  const sessionKey = questionChatKey(ask.conversationId);
  const session = useChatSession(sessionKey);
  const defaultScope = useMemo(
    () => ({ userIds: userOptions.map(user => user.id), processSlugs: [], window: { days: 30 } }),
    [userOptions],
  );
  const fallbackScope =
    !ask.followingPage && ask.question.scope ? ask.question.scope : defaultScope;
  const fallback: ContextualAsk = {
    sessionKey: "ask:general",
    customScope: fallbackScope,
    label: scopeSummary(fallbackScope, null),
    filters: filtersForScope(fallbackScope, processOptions, anchor),
    suggestions: defaultAskSuggestions(t),
  };
  const target = ask.target ?? fallback;
  const starters = askStarters(target, t);
  const [comparison, setComparison] = useState<{
    kind: Exclude<AskAction, "kpi">;
    target: ContextualAsk;
  } | null>(null);
  const updateMentionScope = useAskScopeUpdate(target, userOptions, next =>
    ask.setContext({ ...next, result: undefined }),
  );
  const mentionProcesses = target.filters.recordingIds
    ? (target.sessionProcesses ?? [])
        .filter(name => name.trim())
        .map(name => ({ id: name, label: name }))
    : processOptions;
  const recordQuestion = (text: string, target: ContextualAsk = ask.target ?? fallback) => {
    const scope = target.customScope ?? editableScope(target.filters, userOptions, processOptions);
    if (recentQuestionsSnapshot().some(item => item.id === ask.conversationId))
      touchRecentQuestion(ask.conversationId, scope, target.label, undefined, target);
    else addRecentQuestion(text, scope, target.label, undefined, target, ask.conversationId);
  };
  useEffect(() => {
    const compose = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          userIds: string[];
          windowDays: number;
          text: string;
        }>
      ).detail;
      const scope = {
        userIds: detail.userIds,
        processSlugs: [],
        window: { days: detail.windowDays },
      };
      if (event.type === "tour:watchlist-setup") ask.openWatch({ scope, prompt: detail.text });
      else {
        ask.setContext({
          ...fallback,
          customScope: scope,
          filters: filtersForScope(scope, processOptions, anchor),
          label: scopeSummary(scope, null),
          empty: !hasProcessSelection(scope),
        });
        setChatDraft(sessionKey, detail.text);
      }
    };
    const scripted = (event: Event) => {
      const detail = (event as CustomEvent<{ question: string; answer: string }>).detail;
      recordQuestion(detail.question);
      playScriptedReply(sessionKey, detail.question, detail.answer);
    };
    window.addEventListener("tour:watchlist-ask", compose);
    window.addEventListener("tour:watchlist-setup", compose);
    window.addEventListener("tour:watchlist-scripted-ask", scripted);
    return () => {
      window.removeEventListener("tour:watchlist-ask", compose);
      window.removeEventListener("tour:watchlist-setup", compose);
      window.removeEventListener("tour:watchlist-scripted-ask", scripted);
    };
  });
  const lastQuestion = [...session.entries]
    .reverse()
    .find(entry => entry.kind === "message" && entry.role === "user");
  const hasAnswer =
    session.status !== "streaming" &&
    session.entries.some(
      entry => entry.kind === "message" && entry.role === "assistant" && entry.content,
    );
  return (
    <>
      <div
        className={ask.watchRequest || comparison ? "hidden" : "h-full min-h-0 p-4 text-sm"}
        data-tour={
          ask.watchRequest
            ? undefined
            : target.sessionKey.startsWith("process:")
              ? "process-chat-panel"
              : "watchlist-ask"
        }
      >
        <ChatPanel
          sessionKey={sessionKey}
          filters={target.filters}
          context={
            [target.context, target.result?.context].filter(Boolean).join("\n\n") || undefined
          }
          scope={{
            label: `${target.label}${target.contextItems?.length ? ` (${target.contextItems.map(item => item.name).join(", ")})` : ""}`,
          }}
          contextItems={target.contextItems}
          onRemoveContextItem={ask.removeContext}
          suggestions={starters?.questions ?? target.suggestions}
          welcome={starters ?? undefined}
          starterAction={
            starters
              ? {
                  label: starters.actionLabel,
                  kind:
                    starters.action === "kpi"
                      ? "kpi"
                      : starters.action === "explore"
                        ? "explore"
                        : "compare",
                  onClick: () => {
                    if (starters.action === "kpi") ask.openWatch({ context: target });
                    else setComparison({ kind: starters.action, target });
                  },
                }
              : undefined
          }
          mentionOptions={{
            users: target.filters.recordingIds
              ? []
              : [
                  { id: "@all-people", label: t("watchlist.all_people"), selectAll: true },
                  ...userOptions,
                ],
            processes: [
              { id: "@all-processes", label: t("watchlist.all_processes"), selectAll: true },
              ...mentionProcesses,
            ],
            onPick: candidate =>
              updateMentionScope(filtersWithMention(target.filters, candidate, userOptions), true),
          }}
          active={active && !ask.watchRequest && !comparison}
          onSend={(text, messageScope) =>
            recordQuestion(
              text,
              messageScope
                ? {
                    ...target,
                    filters: messageScope.filters,
                    label: messageScope.label,
                  }
                : target,
            )
          }
          disabled={
            target.empty ||
            target.filters.userIds?.length === 0 ||
            target.filters.processes?.length === 0 ||
            (!ask.target && !hasProcessSelection(fallbackScope))
          }
          composerScope={
            <div className="space-y-2">
              {target.result && (
                <div className="rounded-lg bg-muted-surface p-2 text-xs text-text-mid">
                  <p>{target.result.label}</p>
                  <p className="mt-1 text-text-dim">
                    {t(target.result.scopeKnown ? "chat_result_data" : "chat_result_scope_unknown")}
                  </p>
                </div>
              )}
              <div className="flex items-start gap-1">
                <AskScopeControls
                  target={target}
                  onChange={ask.setContext}
                  userOptions={userOptions}
                  processOptions={processOptions}
                />
                {ask.page && !ask.followingPage && (
                  <button
                    type="button"
                    onClick={ask.followPage}
                    aria-label={t("askPanel.use_page")}
                    title={t("askPanel.use_page")}
                    className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-info hover:bg-info/5 focus-visible:outline-info"
                  >
                    <RotateCcw size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          }
          composerActions={
            alert ? (
              <ConversationAlert alert={alert} />
            ) : (
              hasAnswer &&
              kpiOfferHiddenIn !== ask.conversationId && (
                <FollowUpAction
                  icon={ChartNoAxesColumn}
                  label={t("chat_track_this")}
                  hint={t("chat_track_this_hint")}
                  className="mt-3"
                  onDismiss={() => setKpiOfferHiddenIn(ask.conversationId)}
                  dismissLabel={t("chat_track_this_dismiss")}
                  onClick={() => {
                    const filters =
                      lastQuestion?.kind === "message" ? lastQuestion.filters : undefined;
                    ask.openWatch({
                      fromConversation: true,
                      context: {
                        ...target,
                        customScope: undefined,
                        filters: filters ?? target.filters,
                        label:
                          lastQuestion?.kind === "message"
                            ? (session.scopeLabel ?? target.label)
                            : target.label,
                      },
                      prompt: lastQuestion?.kind === "message" ? lastQuestion.content : undefined,
                    });
                  }}
                />
              )
            )
          }
        />
      </div>
      {comparison && !ask.watchRequest && (
        <AskProcessActionSetup
          target={
            comparison.kind === "processes" && target.sessionKey === comparison.target.sessionKey
              ? target
              : comparison.target
          }
          kind={comparison.kind}
          userOptions={userOptions}
          processOptions={processOptions}
          onCancel={() => setComparison(null)}
          onOpen={(slug, action) => {
            setComparison(null);
            ask.close();
            navigate(`/processes/${encodeURIComponent(slug)}/`, {
              state: { processAction: action },
            });
          }}
        />
      )}
      {ask.watchRequest && (
        <AskWatchSetup
          key={ask.watchRequest.id}
          request={ask.watchRequest}
          entries={ask.watchRequest.fromConversation ? session.entries : undefined}
          onClose={() => {
            ask.finishWatch();
          }}
          onCreated={id => {
            if (ask.watchRequest?.fromConversation) {
              attachQuestionAlert(ask.conversationId, id);
              ask.finishWatch();
            }
          }}
        />
      )}
    </>
  );
}
